import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getBillingPeriods,
  createBillingPeriod,
  getBillingPeriod,
  updateBillingLine,
  advanceBillingStatus,
  deleteBillingPeriod,
  createRevisedBillingPeriod,
  refreshFromSchedule,
  releaseRetainage,
  unreleaseRetainage,
  getSOVLines,
  getProgressAlerts
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';

/* ═══════════════════════════════════════════════
   CONSTANTS & HELPERS
   ═══════════════════════════════════════════════ */
const C = {
  slate900:'#0f172a', slate700:'#334155', slate500:'#64748b', slate400:'#94a3b8', slate200:'#e2e8f0', slate100:'#f1f5f9', slate50:'#f8fafc',
  blue600:'#2563eb', blue500:'#3b82f6', blue50:'#eff6ff',
  green600:'#16a34a', green50:'#f0fdf4',
  amber600:'#d97706', amber50:'#fffbeb',
  rose600:'#dc2626', rose50:'#fef2f2',
  white:'#fff', indigo:'#6366f1',
};

const $f = (v) => {
  if (v === null || v === undefined) return '$0.00';
  return '$' + parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const $p = (v) => parseFloat(v || 0).toFixed(1) + '%';
const $n = (v) => parseFloat(v) || 0;

// Format YYYY-MM-DD string as locale date without timezone shift
const $d = (dateStr) => {
  if (!dateStr) return '–';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};

const STATUS_FLOW = ['Draft', 'Finalized'];
const NEXT_ACTION = { Draft: 'Finalize & Send to Accounting' };

const statusColor = (s) => {
  switch (s?.toLowerCase()) {
    case 'draft': return { bg: C.slate100, fg: C.slate500, border: C.slate200 };
    case 'finalized': return { bg: C.green50, fg: C.green600, border: '#86efac' };
    // Legacy statuses for any existing data
    case 'reviewed': return { bg: C.blue50, fg: C.blue600, border: '#93c5fd' };
    case 'exported': return { bg: C.amber50, fg: C.amber600, border: '#fcd34d' };
    case 'locked': return { bg: C.green50, fg: C.green600, border: '#86efac' };
    default: return { bg: C.slate100, fg: C.slate500, border: C.slate200 };
  }
};

/* ═══════════════════════════════════════════════
   AIA G703 COLUMN DEFINITIONS
   ═══════════════════════════════════════════════ */
const G703_COLS = [
  { key:'item',  label:'Item No.',            aiaCol:'A', w:'70px',  align:'left' },
  { key:'desc',  label:'Description of Work',  aiaCol:'B', w:'1fr',   align:'left' },
  { key:'sched', label:'Scheduled Value',       aiaCol:'C', w:'110px', align:'right' },
  { key:'prev',  label:'From Previous Application', aiaCol:'D', w:'110px', align:'right' },
  { key:'work',  label:'This Period (Work)',     aiaCol:'E', w:'100px', align:'right' },
  { key:'mat',   label:'Materials Stored',       aiaCol:'F', w:'100px', align:'right' },
  { key:'total', label:'Total Completed & Stored', aiaCol:'G', w:'120px', align:'right' },
  { key:'gpct',  label:'% (G/C)',               aiaCol:'',  w:'60px',  align:'right' },
  { key:'bal',   label:'Balance to Finish',      aiaCol:'H', w:'100px', align:'right' },
  { key:'ret',   label:'Retainage',              aiaCol:'I', w:'100px',  align:'right' },
  { key:'retRel', label:'Ret. Released',         aiaCol:'',  w:'130px',  align:'right' },
];

const gridTemplate = G703_COLS.map(c => c.w).join(' ');

/* ═══════════════════════════════════════════════
   EDITABLE CELL
   ═══════════════════════════════════════════════ */
const EditCell = ({ value, onChange, disabled, type = 'number', suffix = '', t }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setVal(value); }, [value]);

  const commit = () => {
    setEditing(false);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed !== parseFloat(value)) {
      onChange(parsed);
    }
  };

  if (disabled || !onChange) {
    return <span style={{ fontSize: 13, color: t.textSecondary }}>{type === 'percent' ? $p(value) : $f(value)}</span>;
  }

  if (!editing) {
    return (
      <span
        onClick={() => { setEditing(true); setTimeout(() => inputRef.current?.select(), 0); }}
        style={{
          fontSize: 13, color: t.heading, cursor: 'pointer', padding: '2px 6px',
          borderRadius: 4, background: '#f0f4ff', border: '1px dashed #93c5fd',
          display: 'inline-block', minWidth: 50, textAlign: 'right',
        }}
        title="Click to edit"
      >
        {type === 'percent' ? $p(value) : $f(value)}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <input
        ref={inputRef}
        type="number"
        step={type === 'percent' ? '0.1' : '0.01'}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(value); setEditing(false); } }}
        style={{
          width: '100%', padding: '2px 6px', fontSize: 13, border: `1px solid ${C.blue500}`,
          borderRadius: 4, outline: 'none', textAlign: 'right', background: t.bg,
          boxShadow: '0 0 0 2px rgba(59,130,246,.15)',
        }}
        autoFocus
      />
      {suffix && <span style={{ fontSize: 11, color: t.textSecondary }}>{suffix}</span>}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
const BillingTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [periods, setPeriods] = useState([]);
  const [selPeriod, setSelPeriod] = useState(null);
  const [details, setDetails] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [prevDetails, setPrevDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({ period_name: '', period_start_date: '', period_end_date: '', invoice_date: '', notes: '' });
  const [releaseModal, setReleaseModal] = useState(null);
  const [releaseForm, setReleaseForm] = useState({ amount: '', notes: '' });
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [progressAlerts, setProgressAlerts] = useState(null);

  const fetchPeriods = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getBillingPeriods(projectId);
      setPeriods(data);
      return data;
    } catch (e) { console.error(e); return []; }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  const fetchDetails = async (periodId) => {
    try {
      const data = await getBillingPeriod(periodId);
      setDetails(data);
    } catch (e) { console.error(e); }
  };

  const fetchAlerts = async (periodId) => {
    try {
      const data = await getProgressAlerts(periodId);
      setProgressAlerts(data);
    } catch (e) { console.error('Failed to fetch progress alerts:', e); setProgressAlerts(null); }
  };

  const selectPeriod = async (p) => {
    setSelPeriod(p);
    // Auto-refresh Draft periods from schedule so lines always reflect latest task progress
    if (p.status === 'Draft') {
      try {
        setRefreshing(true);
        await refreshFromSchedule(p.id);
      } catch (e) { console.error('Auto-refresh from schedule failed:', e); }
      finally { setRefreshing(false); }
    }
    fetchDetails(p.id);
    fetchAlerts(p.id);
  };

  /* ═══ RETAINAGE RELEASE ═══ */
  const openReleaseModal = async (line) => {
    try {
      // Use the billing line's retainage_amount (cumulative, matches what's displayed)
      // rather than recalculating from SOV billed fields which may differ
      const totalHeld = parseFloat(line.retainage_amount) || 0;
      const alreadyReleased = parseFloat(line.retainage_released_this_period) || 0;
      setReleaseModal({
        sovLineId: line.sov_line_id,
        lineDesc: line.sov_description,
        sovCode: line.sov_code,
        totalHeld,
        alreadyReleased,
        available: Math.max(0, totalHeld - alreadyReleased),
      });
      setReleaseForm({ amount: alreadyReleased > 0 ? alreadyReleased.toFixed(2) : '', notes: '' });
    } catch (err) {
      console.error('Failed to open release modal:', err);
    }
  };

  const handleSetRetainageRelease = async () => {
    if (!releaseModal) return;
    const targetAmt = parseFloat(releaseForm.amount);
    if (isNaN(targetAmt) || targetAmt < 0) return;
    if (targetAmt > releaseModal.totalHeld + 0.01) return;

    const currentReleased = releaseModal.alreadyReleased;
    const delta = targetAmt - currentReleased;

    // No change needed
    if (Math.abs(delta) < 0.01) { setReleaseModal(null); return; }

    try {
      setReleaseLoading(true);
      const prevReleased = currentReleased;

      if (delta > 0) {
        // Need to release more
        await releaseRetainage(releaseModal.sovLineId, { amount: delta, notes: releaseForm.notes });
      } else {
        // Need to unrelease (write back)
        await unreleaseRetainage(releaseModal.sovLineId, { amount: Math.abs(delta) });
      }

      // Push onto undo stack with previous value so we can restore it
      setUndoStack(prev => [...prev, {
        type: 'retainage_set',
        sovLineId: releaseModal.sovLineId,
        previousReleased: prevReleased,
        newReleased: targetAmt,
      }]);
      setRedoStack([]);
      setReleaseModal(null);

      if (selPeriod) {
        if (selPeriod.status === 'Draft') await refreshFromSchedule(selPeriod.id);
        await fetchDetails(selPeriod.id);
      }
      refreshSummary();
    } catch (err) {
      console.error('Failed to set retainage release:', err);
    } finally {
      setReleaseLoading(false);
    }
  };

  /* CRUD */
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createBillingPeriod({ ...form, project_id: projectId });
      setShowCreate(false);
      setForm({ period_name: '', period_start_date: '', period_end_date: '', invoice_date: '', notes: '' });
      await fetchPeriods();
      refreshSummary();
    } catch (e) { console.error(e); }
  };

  const handleUpdateLine = async (lineId, field, value) => {
    try {
      // Save previous value for undo
      const prevLine = details?.lines?.find(l => l.id === lineId);
      const prevValue = prevLine ? prevLine[field] : null;
      setUndoStack(prev => [...prev, { lineId, field, value: prevValue }]);
      setRedoStack([]); // clear redo on new edit

      await updateBillingLine(lineId, { [field]: value });
      await fetchDetails(selPeriod.id);
      refreshSummary();
    } catch (e) { console.error(e); }
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    try {
      if (last.type === 'retainage_set') {
        // Restore to previous released amount
        const delta = last.previousReleased - last.newReleased;
        if (delta > 0.005) {
          await releaseRetainage(last.sovLineId, { amount: delta, notes: '(undo)' });
        } else if (delta < -0.005) {
          await unreleaseRetainage(last.sovLineId, { amount: Math.abs(delta) });
        }
        setRedoStack(prev => [...prev, last]);
        if (selPeriod?.status === 'Draft') await refreshFromSchedule(selPeriod.id);
      } else {
        // Regular field edit undo
        const curLine = details?.lines?.find(l => l.id === last.lineId);
        const curValue = curLine ? curLine[last.field] : null;
        setRedoStack(prev => [...prev, { lineId: last.lineId, field: last.field, value: curValue }]);
        await updateBillingLine(last.lineId, { [last.field]: last.value });
      }
      await fetchDetails(selPeriod.id);
      refreshSummary();
    } catch (e) { console.error(e); }
  };

  const handleRedo = async () => {
    if (redoStack.length === 0) return;
    const last = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    try {
      if (last.type === 'retainage_set') {
        // Re-apply the target amount
        const delta = last.newReleased - last.previousReleased;
        if (delta > 0.005) {
          await releaseRetainage(last.sovLineId, { amount: delta, notes: '(redo)' });
        } else if (delta < -0.005) {
          await unreleaseRetainage(last.sovLineId, { amount: Math.abs(delta) });
        }
        setUndoStack(prev => [...prev, last]);
        if (selPeriod?.status === 'Draft') await refreshFromSchedule(selPeriod.id);
      } else {
        // Regular field edit redo
        const curLine = details?.lines?.find(l => l.id === last.lineId);
        const curValue = curLine ? curLine[last.field] : null;
        setUndoStack(prev => [...prev, { lineId: last.lineId, field: last.field, value: curValue }]);
        await updateBillingLine(last.lineId, { [last.field]: last.value });
      }
      await fetchDetails(selPeriod.id);
      refreshSummary();
    } catch (e) { console.error(e); }
  };

  const handleAdvance = async () => {
    if (!selPeriod) return;
    const next = NEXT_ACTION[selPeriod.status];
    if (!next) return;

    if (selPeriod.status === 'Draft') {
      const prev = periods.find(p => p.id !== selPeriod.id && p.status !== 'Draft');
      if (prev) { try { setPrevDetails(await getBillingPeriod(prev.id)); } catch { setPrevDetails(null); } }
      else { setPrevDetails(null); }
      setShowCompare(true);
      return;
    }

    await doAdvance();
  };

  const doAdvance = async () => {
    try {
      const wasDraft = selPeriod.status === 'Draft';
      const periodId = selPeriod.id;
      await advanceBillingStatus(periodId);
      const ps = await fetchPeriods();
      const u = ps?.find(p => p.id === periodId);
      if (u) { setSelPeriod(u); await fetchDetails(u.id); }
      refreshSummary();

      // On finalization, download the CE export CSV for the accounting agent
      if (wasDraft) {
        const link = document.createElement('a');
        link.href = `http://localhost:5001/api/billing/${periodId}/ce-export`;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) { console.error(e); alert(e.message || 'Failed'); }
  };

  // refreshFromSchedule is now called automatically when selecting a Draft period

  const handleDelete = async () => {
    if (!selPeriod) return;
    const ok = await confirm({ title: 'Delete Billing Period', message: 'Are you sure? This cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await deleteBillingPeriod(selPeriod.id);
      setSelPeriod(null); setDetails(null);
      await fetchPeriods(); refreshSummary();
    } catch (e) { console.error(e); }
  };

  const handleRevise = async () => {
    if (!selPeriod) return;
    try {
      const revised = await createRevisedBillingPeriod(selPeriod.id);
      await fetchPeriods();
      setSelPeriod(revised);
      await fetchDetails(revised.id);
    } catch (e) { console.error(e); alert(e.message || 'Failed'); }
  };

  /* Computed */
  const isDraft = selPeriod?.status === 'Draft';
  const isLocked = selPeriod?.status === 'Finalized' || selPeriod?.status === 'Exported' || selPeriod?.status === 'Locked';
  const nextAction = NEXT_ACTION[selPeriod?.status];

  const totals = (() => {
    if (!details?.lines) return {};
    const lines = details.lines;
    const sched = lines.reduce((s, l) => s + $n(l.contract_amount), 0);
    const prevBilled = lines.reduce((s, l) => s + $n(l.billed_to_date), 0);
    const thisWork = lines.reduce((s, l) => s + $n(l.this_period_earned_amount), 0);
    const stored = lines.reduce((s, l) => s + $n(l.stored_materials_this_period), 0);
    const totalComp = lines.reduce((s, l) => s + $n(l.total_completed_and_stored), 0);
    const ret = lines.reduce((s, l) => s + $n(l.retainage_amount), 0);
    const retReleased = lines.reduce((s, l) => s + $n(l.retainage_released_this_period), 0);
    const bal = lines.reduce((s, l) => s + $n(l.remaining_balance), 0);
    const net = lines.reduce((s, l) => s + $n(l.total_billable_this_period), 0);
    return { sched, prevBilled, thisWork, stored, totalComp, ret, retReleased, bal, net, pct: sched > 0 ? (totalComp / sched * 100) : 0 };
  })();

  const appNumber = (() => {
    if (!selPeriod) return '';
    const sorted = [...periods].sort((a, b) => a.period_start_date.localeCompare(b.period_start_date));
    const idx = sorted.findIndex(p => p.id === selPeriod.id);
    return idx >= 0 ? idx + 1 : '';
  })();

  /* Shared styles */
  const card = { background: t.bg, borderRadius: 16, border: `1px solid ${t.cardBorder}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };
  const mInput = { width: '100%', padding: '10px 14px', fontSize: 14, border: `1px solid ${t.cardBorder}`, borderRadius: 10, outline: 'none', transition: 'border-color .2s', backgroundColor: t.bg, boxSizing: 'border-box' };
  const mLabel = { display: 'block', fontSize: 14, fontWeight: 600, color: t.heading, marginBottom: 6 };
  const mGroup = { marginBottom: 14 };

  return (
    <div>
      {/* ═══ CREATE MODAL ═══ */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: t.bg, borderRadius: 16, width: 520, maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px rgba(0,0,0,.2), 0 0 0 1px rgba(0,0,0,.05)', animation: 'slideUp .25s ease',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: t.heading }}>New Pay Application</h2>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: t.textSecondary }}>Create a new monthly billing period.</p>
                </div>
                <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textSecondary, fontSize: 22, lineHeight: 1 }}>&times;</button>
              </div>
            </div>
            <form onSubmit={handleCreate} style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
              <div style={mGroup}>
                <label style={mLabel}>Period Name *</label>
                <input type="text" value={form.period_name} onChange={e => setForm({ ...form, period_name: e.target.value })} required placeholder="e.g., Pay App #3 - March 2026" style={mInput} onFocus={e => e.target.style.borderColor = C.blue500} onBlur={e => e.target.style.borderColor = t.cardBorder} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={mGroup}><label style={mLabel}>Period Start *</label><input type="date" value={form.period_start_date} onChange={e => {
                  const startVal = e.target.value;
                  const updates = { ...form, period_start_date: startVal };
                  // Auto-default end date to last day of start month so the calendar opens in the right month
                  if (startVal && !form.period_end_date) {
                    const [y, m] = startVal.split('-').map(Number);
                    const lastDay = new Date(y, m, 0).getDate();
                    const endDate = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
                    updates.period_end_date = endDate;
                    // Also default invoice date so its calendar opens in the same month
                    if (!form.invoice_date) {
                      updates.invoice_date = endDate;
                    }
                  }
                  setForm(updates);
                }} required style={mInput} /></div>
                <div style={mGroup}><label style={mLabel}>Period End *</label><input type="date" value={form.period_end_date} onChange={e => {
                  const endVal = e.target.value;
                  const updates = { ...form, period_end_date: endVal };
                  // Auto-default application date to the end date so the calendar opens in the right month
                  if (endVal && !form.invoice_date) {
                    updates.invoice_date = endVal;
                  }
                  setForm(updates);
                }} required min={form.period_start_date || undefined} style={mInput} /></div>
              </div>
              <div style={mGroup}><label style={mLabel}>Application Date *</label><input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} required min={form.period_start_date || undefined} style={mInput} /></div>
              <div style={mGroup}><label style={mLabel}>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows="2" placeholder="Optional notes..." style={{ ...mInput, resize: 'vertical', fontFamily: 'inherit' }} /></div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 10, borderTop: `1px solid ${t.tableBorder}` }}>
                <button type="button" onClick={() => setShowCreate(false)} style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.textSecondary, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: 'none', backgroundColor: C.blue500, color: C.white, cursor: 'pointer' }}>Create Pay App</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ HEADER ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: t.heading, letterSpacing: '-.4px' }}>Pay Applications</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: t.textSecondary }}>AIA G702/G703 style billing with per-line retainage.</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
          border: 'none', backgroundColor: C.blue500, color: C.white, cursor: 'pointer', boxShadow: '0 2px 8px rgba(59,130,246,.25)',
        }}>+ New Pay App</button>
      </div>

      {/* ═══ MAIN LAYOUT ═══ */}
      <div style={{ display: 'flex', gap: 20, minHeight: 500 }}>

        {/* ─── LEFT: Periods sidebar ─── */}
        <div style={{ width: 260, flexShrink: 0, border: `1px solid ${t.cardBorder}`, borderRadius: 14, backgroundColor: t.bg, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${t.cardBorder}`, backgroundColor: t.bgAlt, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary }}>
            All Periods ({periods.length})
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: 14 }}>Loading...</div>
            ) : periods.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: 14 }}>No pay applications yet.</div>
            ) : (
              periods.map(p => {
                const active = selPeriod?.id === p.id;
                const sc = statusColor(p.status);
                return (
                  <div key={p.id} onClick={() => selectPeriod(p)} style={{
                    padding: '14px 16px', cursor: 'pointer', borderBottom: `1px solid ${t.tableBorder}`,
                    backgroundColor: active ? C.blue50 : 'transparent',
                    borderLeft: active ? `3px solid ${C.blue500}` : '3px solid transparent',
                    transition: 'background-color .15s',
                  }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = '#fafbfc'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.heading, marginBottom: 4 }}>{p.period_name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: sc.bg, color: sc.fg, border: `1px solid ${sc.border}` }}>{p.status}</span>
                    </div>
                    <div style={{ fontSize: 11, color: t.textSecondary }}>
                      {$d(p.period_start_date)} – {$d(p.period_end_date)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ─── RIGHT: Pay App Detail ─── */}
        <div style={{ flex: 1, minWidth: 0, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
          {selPeriod && details ? (
            <>
              {/* AIA G702 Header Card */}
              <div style={{ ...card, padding: '20px 28px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: t.textSecondary, marginBottom: 4 }}>Application for Payment</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: t.heading }}>{selPeriod.period_name}</h3>
                      {(() => { const sc = statusColor(selPeriod.status); return <span style={{ padding: '3px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, backgroundColor: sc.bg, color: sc.fg, border: `1px solid ${sc.border}` }}>{selPeriod.status}</span>; })()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {isDraft && <button onClick={handleDelete} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #fecaca', backgroundColor: C.white, color: C.rose600, cursor: 'pointer' }}>Delete</button>}
                    {isDraft && refreshing && <span style={{ fontSize: 12, color: t.textSecondary, fontStyle: 'italic' }}>Syncing from schedule...</span>}
                    {!isLocked && nextAction && <button onClick={handleAdvance} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', backgroundColor: C.blue500, color: C.white, cursor: 'pointer', boxShadow: '0 2px 8px rgba(59,130,246,.2)' }}>{nextAction}</button>}
                    {!isDraft && <button onClick={handleRevise} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.heading, cursor: 'pointer' }}>Create Revised</button>}
                  </div>
                </div>

                {/* AIA Info Row */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13, color: t.textSecondary }}>
                  {appNumber && <div><span style={{ fontWeight: 600, color: t.text }}>Application No:</span> {appNumber}</div>}
                  <div><span style={{ fontWeight: 600, color: t.text }}>Period To:</span> {$d(selPeriod.period_end_date)}</div>
                  {selPeriod.invoice_date && <div><span style={{ fontWeight: 600, color: t.text }}>Application Date:</span> {$d(selPeriod.invoice_date)}</div>}
                </div>
              </div>

              {/* Notes */}
              {selPeriod.notes && (
                <div style={{ padding: '12px 20px', borderRadius: 10, border: `1px solid ${t.cardBorder}`, backgroundColor: C.amber50, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                  <span style={{ fontWeight: 600 }}>Notes:</span> {selPeriod.notes}
                </div>
              )}

              {/* Status Flow */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16, padding: '12px 20px', borderRadius: 10, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg }}>
                {STATUS_FLOW.map((s, i) => {
                  const ci = STATUS_FLOW.indexOf(selPeriod.status);
                  const done = i < ci;
                  const cur = i === ci;
                  return (
                    <React.Fragment key={s}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                          backgroundColor: done ? C.green600 : cur ? C.blue500 : t.cardBorder,
                          color: done || cur ? C.white : t.textSecondary,
                        }}>{done ? '✓' : i + 1}</div>
                        <span style={{ fontSize: 13, fontWeight: cur ? 700 : 500, color: done ? C.green600 : cur ? t.heading : t.textSecondary }}>{s}</span>
                      </div>
                      {i < STATUS_FLOW.length - 1 && <div style={{ flex: 1, height: 2, margin: '0 12px', backgroundColor: done ? C.green600 : t.cardBorder }} />}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* G702 Summary Cards */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                {[
                  { label: 'Original Contract Sum', value: $f(totals.sched), color: t.heading },
                  { label: 'Total Completed & Stored', value: $f(totals.totalComp), sub: $p(totals.pct), color: C.blue600 },
                  { label: 'Retainage', value: $f(totals.ret), color: C.amber600 },
                  { label: 'Current Payment Due', value: $f(totals.net), color: C.green600 },
                  { label: 'Balance to Finish', value: $f(totals.bal), color: t.text },
                ].map(c => (
                  <div key={c.label} style={{ flex: 1, minWidth: 140, padding: '16px 20px', borderRadius: 12, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 6 }}>{c.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
                    {c.sub && <div style={{ fontSize: 12, color: t.textSecondary, marginTop: 2 }}>{c.sub}</div>}
                  </div>
                ))}
              </div>

              {/* ═══ PROGRESS ALERTS BANNER ═══ */}
              {progressAlerts && progressAlerts.total_alerts > 0 && (
                <div style={{
                  padding: '12px 16px', marginBottom: 12, borderRadius: 10,
                  border: `1px solid ${progressAlerts.critical_count > 0 ? '#fca5a5' : '#fde68a'}`,
                  backgroundColor: progressAlerts.critical_count > 0 ? '#fef2f2' : '#fffbeb',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
                    {progressAlerts.critical_count > 0 ? '⚠️' : '📋'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: progressAlerts.critical_count > 0 ? '#dc2626' : '#92400e', marginBottom: 4 }}>
                      {progressAlerts.total_alerts} task{progressAlerts.total_alerts !== 1 ? 's' : ''} need progress updates
                      {progressAlerts.days_until_end <= 7 && (
                        <span style={{ fontWeight: 500, marginLeft: 6 }}>
                          — {progressAlerts.days_until_end <= 0 ? 'Period has ended' : `${progressAlerts.days_until_end} day${progressAlerts.days_until_end !== 1 ? 's' : ''} until period end`}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#78716c', lineHeight: 1.5 }}>
                      {progressAlerts.critical_count > 0 && `${progressAlerts.critical_count} critical (20%+ behind). `}
                      {progressAlerts.warning_count > 0 && `${progressAlerts.warning_count} warning (10%+ behind). `}
                      Update task completion percentages or adjust task dates to reflect actual progress.
                    </div>
                    <details style={{ marginTop: 6, fontSize: 12 }}>
                      <summary style={{ cursor: 'pointer', color: '#92400e', fontWeight: 600 }}>View details</summary>
                      <div style={{ marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                        {progressAlerts.alerts.map((a, i) => (
                          <div key={i} style={{
                            padding: '6px 10px', marginBottom: 4, borderRadius: 6,
                            backgroundColor: a.severity === 'critical' ? '#fee2e2' : a.severity === 'warning' ? '#fef9c3' : '#f0fdf4',
                            border: `1px solid ${a.severity === 'critical' ? '#fecaca' : a.severity === 'warning' ? '#fde68a' : '#bbf7d0'}`,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}>
                            <div>
                              <span style={{ fontWeight: 600, color: '#44403c' }}>{a.task_code || a.task_name}</span>
                              <span style={{ color: '#78716c', marginLeft: 6 }}>{a.sov_code}</span>
                              <span style={{ color: '#78716c', marginLeft: 6 }}>— {a.message}</span>
                            </div>
                            <div style={{
                              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                              backgroundColor: a.severity === 'critical' ? '#dc2626' : a.severity === 'warning' ? '#d97706' : '#16a34a',
                              color: '#fff',
                            }}>
                              {a.actual_percent.toFixed(0)}% / {a.expected_percent.toFixed(0)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                </div>
              )}

              {/* ═══ AIA G703 CONTINUATION SHEET ═══ */}
              <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: 14, backgroundColor: t.bg }}>
                {/* Table title — sticky */}
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.cardBorder}`, backgroundColor: t.bgAlt, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 12, borderRadius: '14px 14px 0 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.heading }}>G703 Continuation Sheet</span>
                    <span style={{ fontSize: 12, color: t.textSecondary }}>{details.lines?.length || 0} line items</span>
                    {isDraft && (
                      <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                        <button
                          onClick={handleUndo}
                          disabled={undoStack.length === 0}
                          title="Undo (Ctrl+Z)"
                          style={{
                            background: 'none', border: `1px solid ${t.cardBorder}`, borderRadius: 6,
                            padding: '3px 8px', cursor: undoStack.length > 0 ? 'pointer' : 'default',
                            color: undoStack.length > 0 ? t.heading : t.textSecondary,
                            opacity: undoStack.length > 0 ? 1 : 0.4, fontSize: 12, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 3,
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                          Undo
                        </button>
                        <button
                          onClick={handleRedo}
                          disabled={redoStack.length === 0}
                          title="Redo (Ctrl+Shift+Z)"
                          style={{
                            background: 'none', border: `1px solid ${t.cardBorder}`, borderRadius: 6,
                            padding: '3px 8px', cursor: redoStack.length > 0 ? 'pointer' : 'default',
                            color: redoStack.length > 0 ? t.heading : t.textSecondary,
                            opacity: redoStack.length > 0 ? 1 : 0.4, fontSize: 12, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 3,
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></svg>
                          Redo
                        </button>
                      </div>
                    )}
                  </div>
                  {isDraft && <span style={{ fontSize: 11, color: C.indigo, fontWeight: 600 }}>Values driven by schedule — update schedule then Refresh</span>}
                </div>

                <div>
                  {/* Header row — sticky */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: gridTemplate,
                    padding: '10px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary,
                    gap: 8, minWidth: 1100, position: 'sticky', top: 49, zIndex: 11,
                  }}>
                    {G703_COLS.map(c => (
                      <div key={c.key} style={{ textAlign: c.align }}>
                        {c.aiaCol && <span style={{ color: C.blue600, marginRight: 4 }}>{c.aiaCol}</span>}
                        {c.label}
                      </div>
                    ))}
                  </div>

                  {/* Data rows */}
                  {(!details.lines || details.lines.length === 0) ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: t.textSecondary, fontSize: 14 }}>No billing lines found.</div>
                  ) : (
                    details.lines.map((line, idx) => {
                      const contract = $n(line.contract_amount);
                      const prevBilled = $n(line.billed_to_date);
                      const thisWork = $n(line.this_period_earned_amount);
                      const storedMat = $n(line.stored_materials_this_period);
                      const totalComp = $n(line.total_completed_and_stored);
                      const gcPct = contract > 0 ? ((prevBilled + totalComp) / contract * 100) : 0;
                      const balance = $n(line.remaining_balance);
                      const retAmt = $n(line.retainage_amount);
                      const retReleased = $n(line.retainage_released_this_period);
                      const lineAlerts = progressAlerts?.alerts?.filter(a => a.sov_line_id === line.sov_line_id) || [];
                      const worstSeverity = lineAlerts.length > 0 ? lineAlerts[0].severity : null;
                      const alertColor = worstSeverity === 'critical' ? '#dc2626' : worstSeverity === 'warning' ? '#d97706' : worstSeverity ? '#f59e0b' : null;
                      return (
                        <div key={line.id}
                          title={worstSeverity ? lineAlerts.map(a => a.message).join('\n') : undefined}
                          style={{
                            display: 'grid', gridTemplateColumns: gridTemplate,
                            padding: '10px 16px',
                            borderBottom: idx < details.lines.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                            borderLeft: alertColor ? `3px solid ${alertColor}` : '3px solid transparent',
                            alignItems: 'center', gap: 8, minWidth: 1100, fontSize: 13,
                            transition: 'background-color .1s',
                            backgroundColor: alertColor ? (worstSeverity === 'critical' ? '#fef2f2' : '#fffbeb') : 'transparent',
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = alertColor ? (worstSeverity === 'critical' ? '#fee2e2' : '#fef3c7') : '#fafbfc'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = alertColor ? (worstSeverity === 'critical' ? '#fef2f2' : '#fffbeb') : 'transparent'}
                        >
                          {/* A: Item */}
                          <div style={{ fontWeight: 600, color: C.blue600 }}>{line.sov_code || '–'}</div>
                          {/* B: Description */}
                          <div style={{ color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={line.sov_description}>{line.sov_description || '–'}</div>
                          {/* C: Scheduled Value */}
                          <div style={{ textAlign: 'right', color: t.text }}>{$f(contract)}</div>
                          {/* D: From Previous */}
                          <div style={{ textAlign: 'right', color: t.textSecondary }}>{$f(prevBilled)}</div>
                          {/* E: This Period Work */}
                          <div style={{ textAlign: 'right', color: t.heading, fontWeight: 500 }}>{$f(thisWork)}</div>
                          {/* F: Materials Stored (editable) */}
                          <div style={{ textAlign: 'right' }}>
                            <EditCell
                              value={storedMat}
                              disabled={!isDraft}
                              onChange={v => handleUpdateLine(line.id, 'stored_materials_this_period', v)}
                              t={t}
                            />
                          </div>
                          {/* G: Total Completed */}
                          <div style={{ textAlign: 'right', color: t.heading, fontWeight: 600 }}>{$f(totalComp)}</div>
                          {/* G/C % + max cap indicator */}
                          <div style={{ textAlign: 'right', color: t.textSecondary }}>
                            {$p(gcPct)}
                            {line.max_billable_percent != null && line.max_billable_percent < 99.9 && (
                              <div style={{ fontSize: 10, color: '#d97706', fontWeight: 600, lineHeight: 1.2 }}
                                title={`Schedule proration: max ${$p(line.max_billable_percent)} billable this period based on task dates`}
                              >
                                max {$p(line.max_billable_percent)}
                              </div>
                            )}
                          </div>
                          {/* H: Balance to Finish */}
                          <div style={{ textAlign: 'right', color: balance === 0 && contract > 0 ? C.green600 : t.text, fontWeight: balance === 0 && contract > 0 ? 600 : 400 }}>{$f(balance)}</div>
                          {/* I: Retainage */}
                          <div style={{ textAlign: 'right', color: C.amber600 }}>{$f(retAmt)}</div>
                          {/* Retainage Released */}
                          {(() => {
                            const retUnreleased = retAmt - retReleased;
                            const isFullyReleased = retAmt > 0 && retUnreleased < 0.01;
                            return (
                              <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                                {retReleased > 0 && (
                                  <span
                                    onClick={() => isDraft && openReleaseModal(line)}
                                    title={isDraft ? "Click to modify retainage release" : ""}
                                    style={{
                                      color: C.green600, fontWeight: 600, fontSize: 12,
                                      cursor: isDraft ? 'pointer' : 'default',
                                      textDecoration: isDraft ? 'underline' : 'none',
                                      textDecorationStyle: 'dotted',
                                      textUnderlineOffset: '2px',
                                    }}
                                    onMouseEnter={e => { if (isDraft) e.currentTarget.style.color = C.green700 || '#15803d'; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = C.green600; }}
                                  >{$f(retReleased)}</span>
                                )}
                                {isDraft && !isFullyReleased && retAmt > 0 && (
                                  <button
                                    onClick={() => openReleaseModal(line)}
                                    title="Release retainage"
                                    style={{
                                      background: 'none', border: `1px solid ${C.amber600}`, borderRadius: 6,
                                      color: C.amber600, fontSize: 10, fontWeight: 600, padding: '2px 6px',
                                      cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.3,
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.amber50; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                  >
                                    Release
                                  </button>
                                )}
                                {isFullyReleased && (
                                  <span style={{ color: C.green600, fontSize: 10, fontWeight: 600 }}>Full</span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })
                  )}

                  {/* Totals row */}
                  {details.lines && details.lines.length > 0 && (
                    <div style={{
                      display: 'grid', gridTemplateColumns: gridTemplate,
                      padding: '14px 16px', backgroundColor: t.bgAlt, borderTop: `2px solid ${t.tableBorder}`,
                      fontSize: 13, fontWeight: 700, color: t.heading, gap: 8, minWidth: 1100,
                    }}>
                      <div style={{ gridColumn: 'span 2' }}>GRAND TOTALS</div>
                      <div style={{ textAlign: 'right' }}>{$f(totals.sched)}</div>
                      <div style={{ textAlign: 'right' }}>{$f(totals.prevBilled)}</div>
                      <div style={{ textAlign: 'right' }}>{$f(totals.thisWork)}</div>
                      <div style={{ textAlign: 'right' }}>{$f(totals.stored)}</div>
                      <div style={{ textAlign: 'right', color: C.blue600 }}>{$f(totals.totalComp)}</div>
                      <div style={{ textAlign: 'right' }}>{$p(totals.pct)}</div>
                      <div style={{ textAlign: 'right' }}>{$f(totals.bal)}</div>
                      <div style={{ textAlign: 'right', color: C.amber600 }}>{$f(totals.ret)}</div>
                      <div style={{ textAlign: 'right', color: totals.retReleased > 0 ? C.green600 : t.textSecondary }}>{totals.retReleased > 0 ? $f(totals.retReleased) : '–'}</div>
                    </div>
                  )}
                </div>
              </div>

            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', border: `1px solid ${t.cardBorder}`, borderRadius: 14, backgroundColor: t.bg, color: t.textSecondary, textAlign: 'center', height: '100%' }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: .5 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.textSecondary, marginBottom: 4 }}>Select a Pay Application</div>
              <div style={{ fontSize: 13, color: t.textSecondary }}>Choose a period from the left or create a new one</div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ COMPARISON MODAL (Review before submit) ═══ */}
      {showCompare && details && (
        <div className="modal-overlay" onClick={() => setShowCompare(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: t.bg, borderRadius: 16, width: '90vw', maxWidth: 1100, maxHeight: '90vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px rgba(0,0,0,.2), 0 0 0 1px rgba(0,0,0,.05)',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: t.heading }}>Review &amp; Finalize Billing</h2>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: t.textSecondary }}>
                    {prevDetails ? `Comparing "${selPeriod.period_name}" against "${prevDetails.period_name}"` : 'First billing period — no previous data to compare'}
                  </p>
                </div>
                <button onClick={() => setShowCompare(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textSecondary, fontSize: 22, lineHeight: 1 }}>&times;</button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
              {(() => {
                const curLines = details.lines || [];
                const pLines = prevDetails?.lines || [];
                const prevBySOV = {};
                pLines.forEach(l => { prevBySOV[l.sov_line_id] = l; });
                // G703 continuation sheet shows GROSS completed & stored (before retainage).
                // Retainage is deducted on the G702 cover sheet, not the continuation.
                const curTotal = curLines.reduce((s, l) => s + $n(l.total_completed_and_stored), 0);
                const prevTotal = pLines.reduce((s, l) => s + $n(l.total_completed_and_stored), 0);
                const curMatStored = curLines.reduce((s, l) => s + $n(l.stored_materials_this_period), 0);
                const curRetainage = curLines.reduce((s, l) => s + $n(l.retainage_amount), 0);
                const curRetRelease = curLines.reduce((s, l) => s + $n(l.retainage_released_this_period), 0);

                return (
                  <>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                      {prevDetails && (
                        <div style={{ flex: 1, minWidth: 160, padding: '14px 18px', borderRadius: 12, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bgAlt }}>
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 4 }}>Previous Period</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: t.textSecondary }}>{$f(prevTotal)}</div>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 160, padding: '14px 18px', borderRadius: 12, border: '1px solid #93c5fd', backgroundColor: '#f0f7ff' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 4 }}>This Period</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: C.blue600 }}>{$f(curTotal)}</div>
                      </div>
                      {prevDetails && (
                        <div style={{ flex: 1, minWidth: 160, padding: '14px 18px', borderRadius: 12, border: `1px solid ${curTotal - prevTotal >= 0 ? '#86efac' : '#fca5a5'}`, backgroundColor: curTotal - prevTotal >= 0 ? C.green50 : C.rose50 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 4 }}>Difference</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: curTotal - prevTotal >= 0 ? C.green600 : C.rose600 }}>{curTotal - prevTotal >= 0 ? '+' : ''}{$f(curTotal - prevTotal)}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 140, padding: '14px 18px', borderRadius: 12, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bgAlt }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 4 }}>Mat. Stored</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: curMatStored > 0 ? t.heading : t.textSecondary }}>{$f(curMatStored)}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 140, padding: '14px 18px', borderRadius: 12, border: '1px solid #fca5a5', backgroundColor: C.rose50 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 4 }}>Retainage Held</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.rose600 }}>{$f(curRetainage)}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 140, padding: '14px 18px', borderRadius: 12, border: '1px solid #86efac', backgroundColor: C.green50 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, marginBottom: 4 }}>Ret. Released</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.green600 }}>{$f(curRetRelease)}</div>
                      </div>
                    </div>

                    <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: 12, overflow: 'hidden', overflowX: 'auto' }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: prevDetails
                          ? '0.5fr 1.3fr 0.5fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr'
                          : '0.5fr 1.3fr 0.5fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr',
                        minWidth: prevDetails ? 900 : 750,
                        padding: '12px 16px', backgroundColor: t.bgAlt, borderBottom: `2px solid ${t.tableBorder}`,
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textSecondary, gap: 6,
                      }}>
                        <div>Code</div><div>Description</div><div style={{ textAlign: 'right' }}>% Comp.</div>
                        {prevDetails && <div style={{ textAlign: 'right' }}>Prev Period</div>}
                        <div style={{ textAlign: 'right' }}>This Period</div>
                        {prevDetails && <div style={{ textAlign: 'right' }}>Change</div>}
                        <div style={{ textAlign: 'right' }}>Mat. Stored</div>
                        <div style={{ textAlign: 'right' }}>Retainage</div>
                        <div style={{ textAlign: 'right' }}>Ret. Release</div>
                        <div style={{ textAlign: 'right' }}>Sched. Value</div>
                      </div>

                      {curLines.map((line, idx) => {
                        const prev = prevBySOV[line.sov_line_id];
                        // Show gross completed & stored (before retainage), matching G703 continuation sheet
                        const thisBill = $n(line.total_completed_and_stored);
                        const prevBill = prev ? $n(prev.total_completed_and_stored) : 0;
                        const diff = thisBill - prevBill;
                        const matStored = $n(line.stored_materials_this_period);
                        const retAmt = $n(line.retainage_amount);
                        const retRelease = $n(line.retainage_released_this_period);
                        return (
                          <div key={line.id} style={{
                            display: 'grid',
                            gridTemplateColumns: prevDetails
                              ? '0.5fr 1.3fr 0.5fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr'
                              : '0.5fr 1.3fr 0.5fr 0.6fr 0.6fr 0.6fr 0.6fr 0.6fr',
                            minWidth: prevDetails ? 900 : 750,
                            padding: '10px 16px', borderBottom: idx < curLines.length - 1 ? `1px solid ${t.tableBorder}` : 'none',
                            alignItems: 'center', gap: 6, backgroundColor: prevDetails && Math.abs(diff) > 0.01 ? C.amber50 : 'transparent',
                          }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.blue600 }}>{line.sov_code || '–'}</div>
                            <div style={{ fontSize: 13, color: t.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.sov_description || '–'}</div>
                            <div style={{ fontSize: 13, textAlign: 'right' }}>{$p(line.current_percent_complete)}</div>
                            {prevDetails && <div style={{ fontSize: 13, color: t.textSecondary, textAlign: 'right' }}>{prev ? $f(prevBill) : '–'}</div>}
                            <div style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{$f(thisBill)}</div>
                            {prevDetails && <div style={{ fontSize: 13, fontWeight: 600, textAlign: 'right', color: diff > 0.01 ? C.green600 : diff < -0.01 ? C.rose600 : t.textSecondary }}>{Math.abs(diff) > 0.01 ? (diff > 0 ? '+' : '') + $f(diff) : '–'}</div>}
                            <div style={{ fontSize: 13, textAlign: 'right', color: matStored > 0 ? t.heading : t.textSecondary }}>{$f(matStored)}</div>
                            <div style={{ fontSize: 13, textAlign: 'right', color: retAmt > 0 ? C.rose600 : t.textSecondary }}>{$f(retAmt)}</div>
                            <div style={{ fontSize: 13, textAlign: 'right', color: retRelease > 0 ? C.green600 : t.textSecondary }}>{$f(retRelease)}</div>
                            <div style={{ fontSize: 13, color: t.textSecondary, textAlign: 'right' }}>{$f(line.contract_amount)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            <div style={{ padding: '16px 28px', borderTop: `1px solid ${t.tableBorder}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowCompare(false)} style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: `1px solid ${t.cardBorder}`, backgroundColor: t.bg, color: t.textSecondary, cursor: 'pointer' }}>Go Back &amp; Edit</button>
              <button onClick={() => { setShowCompare(false); setPrevDetails(null); doAdvance(); }} style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: 'none', backgroundColor: C.green600, color: C.white, cursor: 'pointer' }}>Finalize &amp; Send to Accounting</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ RETAINAGE RELEASE MODAL ═══ */}
      {releaseModal && (
        <div className="modal-overlay" onClick={() => setReleaseModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: t.bg, borderRadius: 16, width: 440, maxHeight: '90vh', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,.25)', border: `1px solid ${t.cardBorder}`,
          }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.cardBorder}` }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.heading }}>Release Retainage</h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: t.textSecondary }}>
                {releaseModal.sovCode ? `${releaseModal.sovCode} — ` : ''}{releaseModal.lineDesc}
              </p>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                <div style={{ background: t.bgAlt, padding: '10px 14px', borderRadius: 10, border: `1px solid ${t.cardBorder}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: t.textSecondary, letterSpacing: 0.5 }}>Total Retainage Held</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.amber600, marginTop: 4 }}>${releaseModal.totalHeld.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div style={{ background: t.bgAlt, padding: '10px 14px', borderRadius: 10, border: `1px solid ${t.cardBorder}` }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: t.textSecondary, letterSpacing: 0.5 }}>Currently Released</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.green600, marginTop: 4 }}>${releaseModal.alreadyReleased.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 6 }}>Release Amount</label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: t.textSecondary }}>
                  Enter the total amount to release (0 to ${releaseModal.totalHeld.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                </p>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={releaseModal.totalHeld}
                  value={releaseForm.amount}
                  onChange={e => setReleaseForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder={`$${releaseModal.alreadyReleased.toFixed(2)}`}
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: 14, border: `1px solid ${t.cardBorder}`,
                    borderRadius: 10, outline: 'none', backgroundColor: t.inputBg || t.bg, color: t.inputColor || t.heading,
                    boxSizing: 'border-box',
                  }}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 6 }}>Notes (optional)</label>
                <textarea
                  value={releaseForm.notes}
                  onChange={e => setReleaseForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g., Substantial completion of framing scope"
                  rows={2}
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: 14, border: `1px solid ${t.cardBorder}`,
                    borderRadius: 10, outline: 'none', resize: 'vertical', backgroundColor: t.inputBg || t.bg,
                    color: t.inputColor || t.heading, boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.cardBorder}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setReleaseModal(null)} style={{
                padding: '8px 18px', fontSize: 13, fontWeight: 600, borderRadius: 10,
                border: `1px solid ${t.cardBorder}`, background: t.bgAlt, color: t.heading, cursor: 'pointer',
              }}>Cancel</button>
              {(() => {
                const targetAmt = parseFloat(releaseForm.amount);
                const isValid = !isNaN(targetAmt) && targetAmt >= 0 && targetAmt <= releaseModal.totalHeld + 0.01;
                const noChange = isValid && Math.abs(targetAmt - releaseModal.alreadyReleased) < 0.01;
                const btnDisabled = releaseLoading || releaseForm.amount === '' || !isValid || noChange;
                return (
                  <button
                    onClick={handleSetRetainageRelease}
                    disabled={btnDisabled}
                    style={{
                      padding: '8px 18px', fontSize: 13, fontWeight: 600, borderRadius: 10,
                      border: 'none', background: C.amber600, color: '#fff', cursor: 'pointer',
                      opacity: btnDisabled ? 0.4 : 1,
                    }}
                  >{releaseLoading ? 'Saving...' : 'Set Release Amount'}</button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default BillingTab;
